import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  PopulatedInteraction,
  Interaction,
  TaskWithPopulatedAssignees,
  EmployeePopulatedGoalIssue,
} from '@momentum/types';
import { Card } from '@/shared/components/ui/Card';
import { GoalReviewCard } from './GoalReviewCard';
import { InteractionWizardDrawer } from './InteractionWizardDrawer';
import { GoalProgressHeader } from './GoalProgressHeader';
import { useEmployee } from '@/shared/hooks/useEmployees';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

type InteractionType = 'praise' | 'feedback' | 'debrief' | null;

interface GoalsReviewWizardProps {
  goals: EmployeePopulatedGoalIssue[];
  interactionsByGoal: Record<string, PopulatedInteraction[]>;
  tasksByGoal: Record<string, TaskWithPopulatedAssignees[]>;
  employeeId: string;
  onComplete?: () => void;
  onInteractionCreated?: (interaction: Interaction, goalId: string) => void;
  lastOneOnOneDate?: string | null;
}

export const GoalsReviewWizard = ({
  goals,
  interactionsByGoal,
  tasksByGoal,
  employeeId,
  onComplete,
  onInteractionCreated,
  lastOneOnOneDate,
}: GoalsReviewWizardProps) => {
  const { t } = useTranslation('oneOnOne');
  const currentEmployee = useEmployeeContext();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCreatingInteraction, setIsCreatingInteraction] = useState(false);
  const [interactionType, setInteractionType] = useState<InteractionType>(null);

  const totalGoals = goals.length;

  const { data: employee } = useEmployee(employeeId, currentEmployee.companyId);

  const currentGoal = goals[currentIndex];
  const currentInteractions =
    (currentGoal && interactionsByGoal[currentGoal._id]) ?? [];
  const currentTasks = (currentGoal && tasksByGoal[currentGoal._id]) ?? [];

  const isLastGoal = currentIndex === totalGoals - 1;

  const handleNext = () => {
    if (currentIndex < totalGoals - 1) {
      setCurrentIndex((index) => index + 1);
      return;
    }
    onComplete?.();
  };

  const handlePrevious = () => {
    setCurrentIndex((index) => Math.max(index - 1, 0));
  };

  const handleCreateInteraction = (type: 'praise' | 'feedback' | 'debrief') => {
    setInteractionType(type);
    setIsCreatingInteraction(true);
  };

  const handleCloseDrawer = () => {
    setIsCreatingInteraction(false);
    setInteractionType(null);
  };

  const handleInteractionComplete = (interaction: Interaction) => {
    if (currentGoal) {
      onInteractionCreated?.(interaction, currentGoal._id);
    }
    handleCloseDrawer();
  };

  if (!totalGoals) {
    return (
      <Card className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-md">
        <p className="text-lg text-muted-foreground">{t('noGoalsAssigned')}</p>
      </Card>
    );
  }

  if (!currentGoal) return null;

  return (
    <div className="space-y-6">
      <GoalProgressHeader
        currentIndex={currentIndex}
        totalGoals={totalGoals}
        employee={employee}
        onPrevious={handlePrevious}
        onNext={handleNext}
        isLastGoal={isLastGoal}
      />

      <GoalReviewCard
        goal={currentGoal}
        interactions={currentInteractions}
        tasks={currentTasks}
        currentEmployeeId={employeeId}
        onCreateInteraction={handleCreateInteraction}
        lastOneOnOneDate={lastOneOnOneDate}
      />

      <InteractionWizardDrawer
        isOpen={isCreatingInteraction}
        interactionType={interactionType}
        goalTitle={currentGoal.title}
        goalId={currentGoal._id}
        onClose={handleCloseDrawer}
        onComplete={handleInteractionComplete}
      />
    </div>
  );
};
